import { FC, useState, useCallback } from 'react';
import { X, Plus, FileText, Image as ImageIcon, FileIcon, Trash2 } from 'lucide-react';
import { RichTextEditor } from '../ui/RichTextEditor';
import { safeHapticImpact, safeHapticNotification } from '../../lib/telegramUtils';
import { getRemainingAppendDays } from '../../lib/interactionAppendUtils';
import { useLanguage } from '../../contexts/LanguageContext';

interface AppendDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (content: string, files?: File[]) => Promise<void>;
  customerName: string;
  completedDate?: string;
  createdDate?: string; // For overdue interactions
  currentDetails?: string;
}

export const AppendDetailsModal: FC<AppendDetailsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  customerName,
  completedDate,
  createdDate,
  currentDetails = ''
}) => {
  useLanguage();
  const [appendContent, setAppendContent] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use completedDate for completed interactions, createdDate for overdue
  // Use completedDate for completed interactions, createdDate for overdue
  const referenceDate = completedDate || createdDate;
  const remainingDays = referenceDate ? getRemainingAppendDays(referenceDate) : 0;
  const isExpired = remainingDays <= 0;

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const fileArray = Array.from(files);

    // Validate file types (images, PDFs, documents)
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    const validFiles = fileArray.filter(file => allowedTypes.includes(file.type));
    const invalidFiles = fileArray.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      setError(`${invalidFiles.length} file(s) skipped. Only images, PDFs, and documents are allowed.`);
      setTimeout(() => setError(null), 5000);
    }

    // Add valid files (max 10 total)
    setUploadedFiles(prev => {
      const newFiles = [...prev, ...validFiles];
      return newFiles.slice(0, 10); // Limit to 10 files
    });

    // Reset input
    event.target.value = '';

    // Haptic feedback
    if (validFiles.length > 0 && safeHapticImpact) {
      safeHapticImpact('light');
    }
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));

    // Haptic feedback
    if (safeHapticImpact) {
      safeHapticImpact('light');
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!appendContent.trim()) {
      setError('Please enter some additional details');
      return;
    }

    if (appendContent.length > 5000) {
      setError('Content exceeds maximum length of 5000 characters');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(appendContent, uploadedFiles.length > 0 ? uploadedFiles : undefined);

      // Success haptic
      if (safeHapticNotification) {
        safeHapticNotification('success');
      }

      // Reset form
      setAppendContent('');
      setUploadedFiles([]);
      onClose();
    } catch (err) {
      console.error('Failed to append details:', err);
      setError('Failed to save additional details. Please try again.');

      // Error haptic
      if (safeHapticNotification) {
        safeHapticNotification('error');
      }
    } finally {
      setIsSaving(false);
    }
  }, [appendContent, uploadedFiles, onSave, onClose]);

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon className="w-5 h-5" />;
    } else if (file.type === 'application/pdf') {
      return <FileText className="w-5 h-5" />;
    } else {
      return <FileIcon className="w-5 h-5" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!isOpen) return null;

  // Show expiry message if window has expired
  if (isExpired && referenceDate) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
        <div className="bg-card rounded-xl w-full max-w-md p-6 border border-border shadow-xl">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <X className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">
              Time Window Expired
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              The 7-day window to add additional details to this interaction has expired.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-muted text-muted-foreground rounded-lg font-medium hover:bg-muted/80 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-card rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-foreground">Add Additional Details</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {customerName} • {remainingDays} day{remainingDays !== 1 ? 's' : ''} to edit
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/50 rounded-full transition-colors"
            disabled={isSaving}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Info Banner - Show remaining days */}
          <div className={`border rounded-lg p-4 ${
            remainingDays <= 2
              ? 'bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-700'
              : 'bg-primary/10 dark:bg-primary/20 border-primary/20 dark:border-primary/30'
          }`}>
            <p className={`text-sm ${
              remainingDays <= 2
                ? 'text-orange-800 dark:text-orange-300'
                : 'text-primary dark:text-primary'
            }`}>
              <strong>{remainingDays <= 2 ? '⚠️ ' : ''}{remainingDays} day{remainingDays !== 1 ? 's' : ''} remaining to edit.</strong>
              {' '}Your additional details will be appended to the existing notes with a timestamp separator.
              You can upload images, PDFs, or documents to provide more context.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Current Details Preview (Optional) */}
          {currentDetails && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-muted-foreground">
                Current Details (read-only) - Last 5 lines
              </label>
              <div className="bg-muted/30 rounded-lg p-4 max-h-40 overflow-y-auto border border-border">
                <div
                  className="text-sm text-muted-foreground prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: currentDetails.split('\n').filter(line => line.trim()).slice(-5).join('\n') || '<em>No details</em>'
                  }}
                />
              </div>
            </div>
          )}

          {/* Append Content Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-muted-foreground">
              Additional Details <span className="text-red-500">*</span>
            </label>
            <RichTextEditor
              value={appendContent}
              onChange={setAppendContent}
              placeholder="Enter additional details about this interaction..."
              className="border border-border rounded-lg"
              minHeight="200px"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Markdown formatting supported</span>
              <span>{appendContent.length} / 5000 characters</span>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-muted-foreground">
              Attach Files (Optional)
            </label>

            {/* File Upload Button */}
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer">
                <Plus className="w-4 h-4" />
                <span>Add Files</span>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploadedFiles.length >= 10}
                />
              </label>
              <span className="text-sm text-muted-foreground">
                {uploadedFiles.length} / 10 files
              </span>
            </div>

            {/* File List */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg border border-border"
                  >
                    <div className="text-primary dark:text-primary">
                      {getFileIcon(file)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                      disabled={isSaving}
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Supported formats: Images (JPG, PNG, GIF, WebP), PDF, Word documents
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-6 py-2 border border-border text-muted-foreground rounded-lg font-medium hover:bg-muted/50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !appendContent.trim()}
            className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Add Details</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
